/**
 * TodoDrawer Component
 * Create/Edit Todo form
 * BuilderBel Construction Project Management System
 */

import { Calendar, ListTodo, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useGetProjectTeamQuery } from '../../services/teamApi';
import {
  useCreateTodoMutation,
  useGetTodoCategoriesQuery,
  useUpdateTodoMutation,
} from '../../services/todoApi';
import type { ProjectMember } from '../../types';
import type {
  CreateTodoRequest,
  Todo,
  TodoPriority,
  TodoStatus,
  UpdateTodoRequest,
} from '../../types/todo';
import { DatePicker, Dropdown, Input, SearchableDropdown, TextArea } from '../forms';

// ============================================================================
// TYPES
// ============================================================================

export interface TodoDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  todo?: Todo | null;
  mode: 'create' | 'edit';
  onSuccess?: () => void;
}

interface TodoFormState {
  title: string;
  description: string;
  priority: TodoPriority;
  category: string;
  assignedTo: string;
  assignedToName: string;
  dueDate: string;
  tags: string;
  notes: string;
  status: TodoStatus;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const DEFAULT_FORM_STATE: TodoFormState = {
  title: '',
  description: '',
  priority: 'medium',
  category: '',
  assignedTo: '',
  assignedToName: '',
  dueDate: '',
  tags: '',
  notes: '',
  status: 'pending',
};

// ============================================================================
// HELPERS
// ============================================================================

const getMemberDisplayName = (member: ProjectMember): string => {
  if (member.user) {
    const { firstName, lastName, email } = member.user;
    if (firstName || lastName) return `${firstName || ''} ${lastName || ''}`.trim();
    if (email) return email;
  }
  return 'Unknown';
};

const getMemberId = (member: ProjectMember): string => {
  return member.userId || member.id || '';
};

// ============================================================================
// COMPONENT
// ============================================================================

const TodoDrawer: React.FC<TodoDrawerProps> = ({ isOpen, onClose, todo, mode, onSuccess }) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  const [form, setForm] = useState<TodoFormState>(DEFAULT_FORM_STATE);
  const [errors, setErrors] = useState<Partial<Record<keyof TodoFormState, string>>>({});

  // API Hooks
  const [createTodo, { isLoading: isCreating }] = useCreateTodoMutation();
  const [updateTodo, { isLoading: isUpdating }] = useUpdateTodoMutation();

  const { data: teamData } = useGetProjectTeamQuery(
    { projectId: projectId || '', limit: 100 },
    { skip: !projectId }
  );

  const { data: categoriesData } = useGetTodoCategoriesQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const isLoading = isCreating || isUpdating;

  // Team member options for assignee dropdown
  const teamMemberOptions =
    teamData?.data?.data?.map((member: ProjectMember) => ({
      value: getMemberId(member),
      label: getMemberDisplayName(member),
    })) || [];

  // Category options
  const categoryOptions = [
    { value: '', label: 'No Category' },
    ...(categoriesData?.data?.map(cat => ({ value: cat.name, label: cat.name })) || []),
  ];

  // Populate form when editing
  useEffect(() => {
    if (mode === 'edit' && todo) {
      setForm({
        title: todo.title || '',
        description: todo.description || '',
        priority: todo.priority || 'medium',
        category: todo.category || '',
        assignedTo:
          typeof todo.assignedTo === 'string'
            ? todo.assignedTo
            : (todo.assignedTo as { _id?: string })?._id || '',
        assignedToName: todo.assignedToName || '',
        dueDate: todo.dueDate ? todo.dueDate.split('T')[0] : '',
        tags: (todo.tags || []).join(', '),
        notes: todo.notes || '',
        status: todo.status || 'pending',
      });
    } else {
      setForm(DEFAULT_FORM_STATE);
    }
    setErrors({});
  }, [mode, todo, isOpen]);

  // ============================================================================
  // FORM HANDLERS
  // ============================================================================

  const setField = <K extends keyof TodoFormState>(key: K, value: TodoFormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: undefined }));
    }
  };

  const handleAssigneeChange = (value: string) => {
    const member = teamData?.data?.data?.find(m => getMemberId(m) === value);
    setForm(prev => ({
      ...prev,
      assignedTo: value,
      assignedToName: member ? getMemberDisplayName(member) : '',
    }));
    if (errors.assignedTo) {
      setErrors(prev => ({ ...prev, assignedTo: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TodoFormState, string>> = {};

    if (!form.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (form.title.trim().length > 500) {
      newErrors.title = 'Title cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;
    if (!projectId) {
      toast.error('Project context required');
      return;
    }

    const tags = form.tags
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    try {
      if (mode === 'create') {
        const createData: CreateTodoRequest = {
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          notes: form.notes.trim() || undefined,
          priority: form.priority,
          category: form.category || undefined,
          tags,
          dueDate: form.dueDate || undefined,
          status: form.status,
        };

        if (form.assignedTo) {
          createData.assignedTo = form.assignedTo;
          createData.assignedToName = form.assignedToName;
        }

        await createTodo({ projectId, data: createData }).unwrap();
        toast.success('To-do created successfully');
      } else if (todo) {
        const updateData: UpdateTodoRequest = {
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          notes: form.notes.trim() || undefined,
          priority: form.priority,
          category: form.category || undefined,
          tags,
          dueDate: form.dueDate || null,
          status: form.status,
        };

        if (form.assignedTo) {
          updateData.assignedTo = form.assignedTo;
          updateData.assignedToName = form.assignedToName;
        }

        await updateTodo({ projectId, todoId: todo._id, data: updateData }).unwrap();
        toast.success('To-do updated successfully');
      }

      onSuccess?.();
      onClose();
    } catch (_error) {
      toast.error(mode === 'create' ? 'Failed to create to-do' : 'Failed to update to-do');
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right"
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'create' ? 'Create To-Do' : 'Edit To-Do'}
      >
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-brand-50 rounded-lg flex items-center justify-center">
                <ListTodo className="h-4 w-4 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {mode === 'create' ? 'New To-Do' : 'Edit To-Do'}
                </h2>
                <p className="text-xs text-gray-500">
                  {mode === 'create' ? 'Create a new action item' : 'Update action item details'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-8 py-6">
            <div className="space-y-5">
              {/* Title */}
              <div>
                <Input
                  label="Title"
                  placeholder="What needs to be done?"
                  value={form.title}
                  onChange={e => setField('title', e.target.value)}
                  error={errors.title}
                  required
                  maxLength={500}
                />
              </div>

              {/* Description */}
              <div>
                <TextArea
                  label="Description"
                  placeholder="Add more details about this to-do..."
                  value={form.description}
                  onChange={val => setField('description', val)}
                  rows={3}
                  maxLength={5000}
                />
              </div>

              {/* Priority and Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Dropdown
                    label="Priority"
                    value={form.priority}
                    onChange={val => setField('priority', val as TodoPriority)}
                    options={PRIORITY_OPTIONS}
                  />
                </div>
                <div>
                  <Dropdown
                    label="Status"
                    value={form.status}
                    onChange={val => setField('status', val as TodoStatus)}
                    options={STATUS_OPTIONS}
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <SearchableDropdown
                  label="Category"
                  placeholder="Select a category..."
                  value={form.category}
                  onChange={val => setField('category', val)}
                  options={categoryOptions}
                />
              </div>

              {/* Assigned To */}
              <div>
                <SearchableDropdown
                  label="Assigned To"
                  placeholder="Search team members..."
                  value={form.assignedTo}
                  onChange={handleAssigneeChange}
                  options={teamMemberOptions}
                  error={errors.assignedTo}
                  emptyMessage="No team members found"
                />
              </div>

              {/* Due Date */}
              <div>
                <div className="flex items-center space-x-1 mb-1">
                  <Calendar className="h-3.5 w-3.5 text-gray-400" />
                  <label className="text-xs text-gray-500 font-medium">Due Date</label>
                </div>
                <DatePicker
                  value={form.dueDate || null}
                  onChange={val => setField('dueDate', val || '')}
                  placeholder="Select due date..."
                />
              </div>

              {/* Tags */}
              <div>
                <Input
                  label="Tags"
                  placeholder="Enter tags separated by commas..."
                  value={form.tags}
                  onChange={e => setField('tags', e.target.value)}
                  hint="Separate multiple tags with commas"
                />
              </div>

              {/* Notes */}
              <div>
                <TextArea
                  label="Notes"
                  placeholder="Internal notes or additional context..."
                  value={form.notes}
                  onChange={val => setField('notes', val)}
                  rows={3}
                />
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-1"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>{mode === 'create' ? 'Creating...' : 'Saving...'}</span>
                </>
              ) : (
                <span>{mode === 'create' ? 'Create To-Do' : 'Save Changes'}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TodoDrawer;
